import type Pusher from 'pusher-js';
import { useEffect, useRef, useState } from 'react';
import { createPusher, isPusherConfigured } from '@/libs/pusher-client';
import { tokenManager } from '@/libs/token-manager';

const API_URL = import.meta.env.VITE_API_URL || 'https://api.fluenceur.local';
const DASHBOARD_PUSHER_AUTH_ENDPOINT = `${API_URL}/dashboard/pusher/auth`;
const DEBUG = import.meta.env.DEV;

const log = DEBUG ? console.log : () => {};
const error = DEBUG ? console.error : () => {};

let sharedPusher: Pusher | null = null;
const subscribedChannels = new Map<string, number>();
const sharedChannels = new Map<string, ReturnType<Pusher['subscribe']>>();
const channelCleanupTimers = new Map<string, ReturnType<typeof setTimeout>>();
let disconnectTimer: ReturnType<typeof setTimeout> | null = null;

const cancelPendingDisconnect = () => {
  if (disconnectTimer === null) return;
  clearTimeout(disconnectTimer);
  disconnectTimer = null;
};

const cancelPendingChannelCleanup = (channelName: string) => {
  const timer = channelCleanupTimers.get(channelName);
  if (timer === undefined) return;
  clearTimeout(timer);
  channelCleanupTimers.delete(channelName);
};

const acquireSharedPusher = (): Pusher | null => {
  if (!isPusherConfigured) return null;

  cancelPendingDisconnect();

  if (!sharedPusher) {
    sharedPusher = createPusher({
      forceTLS: true,
      enabledTransports: ['ws', 'wss'],
      authEndpoint: DASHBOARD_PUSHER_AUTH_ENDPOINT,
      channelAuthorization: {
        endpoint: DASHBOARD_PUSHER_AUTH_ENDPOINT,
        transport: 'ajax',
        params: {},
        headers: {},
        headersProvider: () => {
          const authorization = tokenManager.getAuthorizationHeader();
          return authorization ? { Authorization: authorization } : {};
        },
      },
    });
  }
  return sharedPusher;
};

const scheduleSharedPusherDisconnect = (pusher: Pusher) => {
  if (sharedPusher !== pusher || subscribedChannels.size > 0 || disconnectTimer !== null) return;

  disconnectTimer = setTimeout(() => {
    disconnectTimer = null;
    if (sharedPusher !== pusher || subscribedChannels.size > 0) return;

    log('[Pusher] Disconnecting shared Pusher instance');
    pusher.disconnect();
    sharedPusher = null;
  }, 0);
};

interface UsePusherOptions<TData = unknown> {
  channelName: string;
  eventName: string;
  callback: (data: TData) => void;
  enabled?: boolean; // Optional flag to skip subscription
}

export const usePusher = <TData = unknown>({
  channelName,
  eventName,
  callback,
  enabled = true,
}: UsePusherOptions<TData>) => {
  const callbackRef = useRef(callback);
  const [pusher, setPusher] = useState<Pusher | null>(null);

  useEffect(() => {
    callbackRef.current = callback;
  }, [callback]);

  useEffect(() => {
    if (!enabled || !channelName || channelName.trim() === '') {
      log('[Pusher] Subscription skipped - enabled:', enabled, 'channelName:', channelName);
      setPusher(null);
      return;
    }

    const activePusher = acquireSharedPusher();
    if (!activePusher) {
      log('[Pusher] Subscription skipped because Pusher is not configured');
      setPusher(null);
      return;
    }

    setPusher(activePusher);
    log('[Pusher] Subscribing to channel:', channelName);
    cancelPendingChannelCleanup(channelName);
    const currentSubscriptions = subscribedChannels.get(channelName) ?? 0;
    const channel = sharedChannels.get(channelName) ?? activePusher.subscribe(channelName);
    sharedChannels.set(channelName, channel);
    subscribedChannels.set(channelName, currentSubscriptions + 1);

    const onSubscriptionSucceeded = () => {
      log('[Pusher] Successfully subscribed to channel:', channelName);
    };

    const onSubscriptionError = (err: unknown) => {
      error('[Pusher] Subscription error:', err);
    };

    const onEvent = (data: TData) => {
      log('[Pusher] Received event:', eventName, 'with data:', data);
      callbackRef.current(data);
    };

    channel.bind('pusher:subscription_succeeded', onSubscriptionSucceeded);
    channel.bind('pusher:subscription_error', onSubscriptionError);
    log('[Pusher] Binding to event:', eventName);
    channel.bind(eventName, onEvent);

    return () => {
      log('[Pusher] Cleaning up subscription for channel:', channelName);
      channel.unbind(eventName, onEvent);
      channel.unbind('pusher:subscription_succeeded', onSubscriptionSucceeded);
      channel.unbind('pusher:subscription_error', onSubscriptionError);
      const nextSubscriptions = (subscribedChannels.get(channelName) ?? 1) - 1;
      if (nextSubscriptions <= 0) {
        subscribedChannels.delete(channelName);
        const cleanupTimer = setTimeout(() => {
          channelCleanupTimers.delete(channelName);
          if (subscribedChannels.has(channelName)) return;
          sharedChannels.delete(channelName);
          activePusher.unsubscribe(channelName);
          scheduleSharedPusherDisconnect(activePusher);
        }, 0);
        channelCleanupTimers.set(channelName, cleanupTimer);
      } else {
        subscribedChannels.set(channelName, nextSubscriptions);
      }
    };
  }, [channelName, enabled, eventName]);

  return pusher;
};
